import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  DomainGroup,
  ManagementOverview,
  ProjectScoreRow,
  TopAttentionRow,
} from '@/lib/api/managementService';

applyPlugin(jsPDF);

// ============================================================
// Excel — per-project scorecard
// ============================================================

export function exportManagementExcel(projects: ProjectScoreRow[]) {
  const rows = projects.map((p) => ({
    'App ID': p.appId,
    'Project': p.appName,
    'LeanIX': p.leanIXId,
    'Domain': p.domain,
    'Criticality': p.criticality || '',
    'Score': p.score ?? '',
    'RAG': p.rag || 'NOT SCANNED',
    'Score Delta': p.scoreDelta ?? '',
    'Critical Findings': p.criticalFindings,
    'Tools Connected': p.toolCoverage?.connected?.length ?? '',
    'Tool Coverage %': p.toolCoverage?.coverage_pct ?? '',
    'Repos': p.repoCount,
    'Last Scan': p.lastScanAt ? new Date(p.lastScanAt).toLocaleString() : '',
    'Onboarded': p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '',
    'Stale': p.stale ? 'yes' : 'no',
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Projects');
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([out], { type: 'application/octet-stream' }), 'sentinel-management-scorecard.xlsx');
}

// ============================================================
// PDF — executive report
// ============================================================

export function exportManagementPdf(overview: ManagementOverview, domains: DomainGroup[], top: TopAttentionRow[]) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let y = 20;

  const addPageIfNeeded = (needed: number) => {
    if (y + needed > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      y = 20;
    }
  };

  const k = overview.kpis;
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Sentinel — Security Overview', margin, y);
  y += 7;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120);
  doc.text(`Generated ${new Date(overview.generatedAt).toLocaleString()}`, margin, y);
  doc.setTextColor(0);
  y += 8;

  const kpiPairs: Array<[string, string]> = [
    ['Overall Security Score', k.avgScore != null ? `${k.avgScore}/100` : '—'],
    ['Total Projects', `${k.totalProjects} (${k.scannedProjects} scanned)`],
    ['Critical Projects', `${k.criticalProjects}`],
    ['Critical Findings', `${k.criticalFindings}`],
    ['Projects Improving', `${k.improving}`],
    ['Needs Attention', `${k.needsAttention}`],
  ];
  const colW = (pageWidth - margin * 2) / 3;
  kpiPairs.forEach(([label, value], i) => {
    const x = margin + (i % 3) * colW;
    if (i % 3 === 0 && i > 0) y += 12;
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(label, x, y);
    doc.setFontSize(13);
    doc.setTextColor(0);
    doc.setFont('helvetica', 'bold');
    doc.text(value, x, y + 5);
    doc.setFont('helvetica', 'normal');
  });
  y += 14;

  const section = (title: string) => {
    addPageIfNeeded(16);
    y += 4;
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(41, 98, 255);
    doc.text(title, margin, y);
    doc.setTextColor(0);
    y += 3;
    doc.setDrawColor(200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;
  };

  section('Health distribution');
  const h = overview.healthDistribution;
  doc.setFontSize(10);
  doc.text(
    `Healthy ${h.green}   Needs attention ${h.amber}   Critical ${h.red}   Not scanned ${h.notScanned}`,
    margin, y,
  );
  y += 8;

  section('Findings by security category');
  (doc as any).autoTable({
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Category', 'Critical', 'High', 'Medium', 'Low', 'Total']],
    body: overview.findingsByCategory.map((c) => [c.label, c.critical, c.high, c.medium, c.low, c.total]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [41, 98, 255] },
  });
  y = (doc as any).lastAutoTable.finalY + 8;

  section('Security posture by domain');
  (doc as any).autoTable({
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Domain', 'Projects', 'Avg score', 'Healthy', 'Attention', 'Critical', 'Not scanned', 'Crit. findings']],
    body: domains.map((d) => [d.domain, d.projectCount, d.avgScore ?? '—', d.green, d.amber, d.red, d.notScanned, d.criticalFindings]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [41, 98, 255] },
  });
  y = (doc as any).lastAutoTable.finalY + 8;

  section('Top projects requiring attention');
  (doc as any).autoTable({
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Project', 'Domain', 'Score', 'RAG', 'Crit. findings', 'Delta']],
    body: top.map((t) => [t.appName, t.domain, t.score ?? '—', t.rag || '—', t.criticalFindings, t.scoreDelta ?? '—']),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [41, 98, 255] },
  });

  doc.save('sentinel-security-overview.pdf');
}
